import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, DatePicker, Form, Input, Select} from 'antd';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {StyledLightKYCModal} from './StyledLightKYCModal';
import {StyledKYCModalTitleNameSpace} from '../KYCModal/StyledKYCModal';
import SpinSmall from '../../SpinSmall';
import AntdCountriesSelect from '../../Select/AntdCountriesSelect';
import PhoneNumberInput from '../../PhoneNumberInput';
import {useConfirmClose, useCurrentUser, useEmployee, useIsAdmin, useLocation, useTaxCodeLabel} from '../../../hooks';
import {bankingActions} from '../../../state/actions/banking.actions';
import {mainActions} from '../../../state/actions/main.actions';
import {bankingUserTypesConstants} from '../../../constants';
import {kycHelpers, objectHelpers, textHelpers} from '../../../utils/helpers';
import {
  monthlyIncomeRangeOptions,
  occupationCategoryOptions,
  personalAssetsRangeOptions,
  titleOptions
} from '../../../utils/options';

const {Item} = Form;

const countrySelectProps = kycHelpers.getCountrySelectProps();
const datePickerProps = kycHelpers.getDatePickerProps('birthday');

const initialFormValues = {
  birthday: undefined,
  birth_country: undefined,
  firstname: '',
  email: '',
  lastname: '',
  monthly_income_range: monthlyIncomeRangeOptions[0].value,
  nationality: undefined,
  occupation_category: undefined,
  personal_assets_range: personalAssetsRangeOptions[0].value,
  phone: '',
  place_of_birth: '',
  tax_residence: undefined,
  tax_number: '',
  title: titleOptions[0].value
}

const LightKYCModal = ({
  onCancel,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'quickStart']);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [form] = Form.useForm();
  const {phone_code: defaultPhoneCode, code: defaultCountryCode} = useLocation();
  const taxCodeLabel = useTaxCodeLabel(form);

  const dispatch = useDispatch();
  const open = useSelector(state => state.main.lightKycWindowProps.open);
  const legalPerson = useSelector(state => state.banking.legalPerson);
  const isAdmin = useIsAdmin();
  const user = useCurrentUser();
  const storedEmployee = useEmployee();
  const employee = useMemo(
    () => ({...storedEmployee, phone_number: user?.phone_number}),
    [storedEmployee, user]
  );
  const employeeId = employee.id;

  useEffect(() => {
    if (defaultCountryCode) {
      form.setFieldsValue({
        birth_country: defaultCountryCode,
        country: defaultCountryCode,
        nationality: defaultCountryCode
      });
    }
  }, [defaultCountryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (legalPerson || !open) return;
    dispatch(bankingActions.getCardsUsers({
      query: {user_type: bankingUserTypesConstants.LEGAL_PERSON},
      successCallback: (users) => {
        if (users.length === 0) return;
        dispatch(bankingActions.storeLegalPersonDetails(users[0]));
      }
    }));
  }, [legalPerson, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const gObjProp = (key) => objectHelpers.getObjProp(employee, key);
    if (employee) {
      const {firstName, lastName} = textHelpers.getNameParts(gObjProp('full_name'));
      form.setFieldsValue({
        firstname: firstName,
        lastname: lastName,
        email: gObjProp('email'),
        phone: gObjProp('phone_number')
      });
    }
  }, [employee, form]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // clear user id if employee has been changed
    if (employeeId && userId === null) setUserId(null);
  }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = (...rest) => {
    dispatch(mainActions.closeLightKycWindow());
    onCancel?.(...rest);
  }

  // What the form shows when untouched: the constants plus the values the effects above fill in.
  const openingFormValues = useMemo(() => {
    const {firstName, lastName} = textHelpers.getNameParts(objectHelpers.getObjProp(employee, 'full_name'));
    return {
      ...initialFormValues,
      ...(defaultCountryCode && {
        birth_country: defaultCountryCode,
        country: defaultCountryCode,
        nationality: defaultCountryCode
      }),
      firstname: firstName,
      lastname: lastName,
      email: objectHelpers.getObjProp(employee, 'email'),
      phone: objectHelpers.getObjProp(employee, 'phone_number')
    };
  }, [defaultCountryCode, employee]);

  const handleConfirmClose = useConfirmClose({form, initialValues: openingFormValues, onClose: handleClose});

  const {
    label,
    title,
    placeholder,
    validationEmailRules,
    validationNameRules,
    validationPhoneRules,
    validationRequiredRules,
    validationTaxRules,
  } = useMemo(() => kycHelpers.getKYCFormDetails({t, fullFormKey: 'quickStart:lightKyc'}), [t]);

  const footer = (
    <Button
      disabled={loading}
      onClick={() => form.submit()}
      size='large'
      type='primary'
    >
      {t('add')}
    </Button>
  );

  const onFinish = (fields) => {
    const data = {
      ...fields,
      birthday: fields.birthday.format('YYYY-MM-DD'),
      email: fields.email.trim(),
      firstname: fields.firstname.trim(),
      lastname: fields.lastname.trim(),
      phone: fields.phone.trim(),
      // additional params
      tos_accepted: true, // I certify that I am only an Italian fiscal resident
      user_type: bankingUserTypesConstants.EMPLOYEE,
      employee_id: employeeId,
    };
    setLoading(true);

    const successCallback = (userId) => {
      dispatch(bankingActions.createTaxResidence({
        data: {
          ...kycHelpers.getTaxResidenceFormData(data),
          user_id: userId
        },
        successCallback: () => {
          // Update cards module after 3 second (the backend should be ready)
          setTimeout(() => isAdmin
            ? dispatch(bankingActions.getCardsList())
            : dispatch(bankingActions.getUserCardList(employeeId)), 3000);
          dispatch(bankingActions.updateCardVerificationStatus());
          setLoading(false);
          handleClose();
        },
        errorCallback: (response) => {
          const formErrors = kycHelpers.getTaxNumberFormErrors(response, t);
          errorCallback(formErrors);
        }
      }));
    }

    const errorCallback = (formErrors) => {
      form.setFields(formErrors);
      setLoading(false);
    }

    const errorUserCallback = (response) => {
      let formErrors = kycHelpers.getUserCreateFormErrors(response, t);
      errorCallback(formErrors);
    }

    if (userId) {
      dispatch(bankingActions.updateUser({
        id: userId,
        data,
        successCallback: () => successCallback(userId),
        errorCallback: errorUserCallback
      }));
    } else {
      dispatch(bankingActions.createUser({
        data,
        successCallback: (user) => {
          const userId = user.id;
          setUserId(userId);
          successCallback(userId);
        },
        errorCallback: errorUserCallback
      }));
    }
  }

  return (
    <StyledLightKYCModal
      forceRender
      footer={footer}
      onClose={handleConfirmClose}
      open={open}
      title={title}
      width={472}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Form
          initialValues={initialFormValues}
          form={form}
          layout='vertical'
          onFinish={onFinish}
          requiredMark={false}
        >
          <StyledKYCModalTitleNameSpace size={16} align='start'>
            <Item
              label={label('title')}
              name='title'
              required
            >
              <Select
                options={titleOptions}
                size='large'
              />
            </Item>
            <Item
              label={label('firstName')}
              name='firstname'
              rules={validationNameRules}
            >
              <Input
                minLength={2}
                maxLength={50}
                placeholder={placeholder('firstName')}
                size='large'
              />
            </Item>
          </StyledKYCModalTitleNameSpace>
          <Item
            label={label('lastName')}
            name='lastname'
            rules={validationNameRules}
          >
            <Input
              minLength={2}
              maxLength={50}
              placeholder={placeholder('lastName')}
              size='large'
            />
          </Item>
          <Item
            label={label('email')}
            name='email'
            rules={validationEmailRules}
          >
            <Input
              placeholder={placeholder('email')}
              size='large'
              type='email'
            />
          </Item>
          <Item
            label={label('phoneNumber')}
            name='phone'
            rules={validationPhoneRules}
          >
            <PhoneNumberInput
              placeholder={placeholder('phoneNumber')}
              phoneCode={defaultPhoneCode}
              size='large'
            />
          </Item>
          <Item
            label={label('dateOfBirth')}
            name='birthday'
            rules={validationRequiredRules}
          >
            <DatePicker
              {...datePickerProps}
              placeholder={placeholder('dateOfBirth')}
            />
          </Item>
          <Item
            label={label('placeOfBirth')}
            name='place_of_birth'
            rules={validationRequiredRules}
          >
            <Input
              placeholder={placeholder('placeOfBirth')}
              size='large'
            />
          </Item>

          <Item
            label={label('countryOfBirth')}
            name='birth_country'
            rules={validationRequiredRules}
          >
            <AntdCountriesSelect
              placeholder={placeholder('countryOfBirth')}
              {...countrySelectProps}
            />
          </Item>
          <Item
            label={label('nationality')}
            name='nationality'
            rules={validationRequiredRules}
          >
            <AntdCountriesSelect
              placeholder={placeholder('nationality')}
              {...countrySelectProps}
            />
          </Item>
          <Item
            label={label('occupationCategory')}
            name='occupation_category'
            rules={validationRequiredRules}
            required
          >
            <Select
              placeholder={placeholder('occupationCategory')}
              options={occupationCategoryOptions}
              size='large'
            />
          </Item>
          <Item
            label={label('personalAssetsRange')}
            name='personal_assets_range'
            rules={validationRequiredRules}
            required
          >
            <Select
              placeholder={placeholder('personalAssetsRange')}
              options={personalAssetsRangeOptions}
              size='large'
            />
          </Item>
          <Item
            label={label('monthlyIncomeRange')}
            name='monthly_income_range'
            rules={validationRequiredRules}
            required
          >
            <Select
              placeholder={placeholder('monthlyIncomeRange')}
              options={monthlyIncomeRangeOptions}
              size='large'
            />
          </Item>
          <Item
            label={label('taxResidence')}
            name='tax_residence'
            rules={validationRequiredRules}
          >
            <AntdCountriesSelect
              placeholder={placeholder('taxResidence')}
              {...countrySelectProps}
            />
          </Item>
          <Item
            label={taxCodeLabel}
            name='tax_number'
            rules={validationTaxRules}
          >
            <Input
              placeholder={placeholder('taxCode')}
              size='large'
            />
          </Item>
        </Form>
      </SpinSmall>
    </StyledLightKYCModal>
  );
}

LightKYCModal.propTypes = {
  onCancel: PropTypes.func
}

export default LightKYCModal;

